// @flow

import React from 'react';
import { Controller } from 'react-hook-form';
import {
  Banner,
  Box,
  Input,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { DatePicker } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { TableBudget } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';

import useIntl from '../../../hooks/useIntl';
import { useCustomize356010Modal } from './hooks';
import { FIELD_PREFIX, HEADER_FIELDS } from './utils';
import messages from './messages';

// Field name -> highlight colour, as the 3560-7 passes for Intacct changes.
const NO_HIGHLIGHTS = Object.freeze({});

type Props = {
  onClose: () => void,
};

// Mounted only while open, so closing discards every edit.
const Customize356010Modal = ({ onClose }: Props) => {
  const intl = useIntl();
  const {
    title,
    currentYearBegin,
    currentYearEnd,
    onCurrentYearBeginChange,
    onCurrentYearEndChange,
    priorYearPreview,
    periodError,
    periodNotice,
    form,
    isLoading,
    isError,
    errorMessage,
    useFormReturn,
    columns,
    sections,
    agingWarnings,
    isGenerating,
    canGenerate,
    canRefresh,
    onRefresh,
    onGenerate,
  } = useCustomize356010Modal({ onClose });

  const headerInputs = [
    { name: HEADER_FIELDS.projectName, label: messages.projectName },
    { name: HEADER_FIELDS.borrowerName, label: messages.borrowerName },
    { name: HEADER_FIELDS.borrowerId, label: messages.borrowerId },
    { name: HEADER_FIELDS.projectNumber, label: messages.projectNumber },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      actionsProps={[
        {
          children: intl.formatMessage(messages.cancel),
          onClick: onClose,
        },
        {
          children: intl.formatMessage(messages.refresh),
          onClick: onRefresh,
          disabled: isLoading || isGenerating || !canRefresh,
        },
        {
          children: intl.formatMessage(messages.generate),
          submitButton: true,
          isSubmitting: isGenerating,
          onClick: onGenerate,
          disabled: !canGenerate,
        },
      ]}
      ModalProps={{
        PaperProps: {
          sx: {
            width: '100%',
            maxWidth: { xs: '100dvw', sm: 'calc(100dvw - 32px)', lg: 'xl' },
            height: '100%',
          },
        },
      }}
    >
      <Stack direction="column" spacing={2}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(6, 1fr)',
            },
          }}
        >
          <DatePicker
            label={intl.formatMessage(messages.currentYearBegin)}
            onChange={onCurrentYearBeginChange}
            required
            value={currentYearBegin}
          />
          <DatePicker
            label={intl.formatMessage(messages.currentYearEnd)}
            onChange={onCurrentYearEndChange}
            required
            value={currentYearEnd}
          />
          {headerInputs.map(({ name, label }) => (
            <Controller
              key={name}
              name={name}
              control={useFormReturn.control}
              render={({ field }) => (
                <Input
                  {...field}
                  label={intl.formatMessage(label)}
                  disabled={!form}
                />
              )}
            />
          ))}
        </Box>
        <Typography variant="bodySmall">{priorYearPreview}</Typography>
        {periodError ? <Banner status="error" message={periodError} /> : null}
        {periodNotice ? <Banner status="info" message={periodNotice} /> : null}
        {agingWarnings.map((warning) => (
          <Banner key={warning} status="warning" message={warning} />
        ))}
        {isLoading ? (
          <Banner
            status="info"
            message={intl.formatMessage(messages.loadingNotice)}
          />
        ) : null}
        {isError ? (
          <Typography
            role="alert"
            variant="bodyMedium"
            sx={{ display: 'block', color: 'error.main' }}
          >
            {errorMessage}
          </Typography>
        ) : (
          <AsyncBox loading={isLoading}>
            {form ? (
              <TableBudget
                fieldNamePrefix={FIELD_PREFIX}
                columns={columns}
                sections={sections}
                useFormReturn={useFormReturn}
                tableBodySize={{ height: 800, width: 1488 }}
                highlightFields={NO_HIGHLIGHTS}
                renderAll={false}
              />
            ) : null}
          </AsyncBox>
        )}
      </Stack>
    </Modal>
  );
};

export default Customize356010Modal;
